import { Bubble } from 'js/bubbleCore/bubbles/types';
import { BubbleFactory } from './bubbles/BubbleFactory';
import { Place } from 'js/bubbleCore/types';
import { IGameData } from 'js/bubbleCore/types';
import { Signal } from 'framework/utils/Signal';
import { RandomDataGenerator } from 'framework/RandomDataGenerator/RandomDataGenerator';
import { ILevelData, parseLevel } from 'js/bubbleCore/levelParser/parseLevel';
import { shoot } from 'js/bubbleCore/handlers/shoot';
import { testShoot, ITestShootResult } from 'js/bubbleCore/handlers/testShoot';
import { IPoint } from 'framework/utils/math-utils';
import { checkPopOutCollisions } from 'js/bubbleCore/handlers/collisions/checkPopOutCollisions';
import { checkShootedGridCollisions } from 'js/bubbleCore/handlers/collisions/checkShootedGridCollisions';
import { updateBubblesInSpace } from 'js/bubbleCore/handlers/updateBubblesInSpace';
import { IEvent, EventType } from 'js/bubbleCore/events';
import { popOutUnhookedBubbles } from 'js/bubbleCore/handlers/popOutUnhookedBubbles';
import { scrollGrid } from 'js/bubbleCore/handlers/scrollGrid';
import { resolveShootedBubbleGridCollisions } from 'js/bubbleCore/handlers/collisions/resolveShootedBubbleGridCollisions';
import { resolveBubblePointZoneCollisions } from 'js/bubbleCore/handlers/collisions/resolveBubblePointZoneCollisions';
import { resolveBubbleBouncerCollisions } from 'js/bubbleCore/handlers/collisions/resolveBubbleBouncerCollisions';
import { handleBubblesInShootingSpace } from 'js/bubbleCore/handlers/handleBubblesInShootingSpace';
import { checkDeathLineCrossing } from 'js/bubbleCore/handlers/checkDeathLineCrossing';
import { handleChain } from 'js/bubbleCore/handlers/handleChain';

// DEBUG
import { fillRow } from 'js/bubbleCore/handlers/fillRow';
import { getAngle, deg2rad, rad2deg } from 'framework/utils/math-utils';


export class BubbleGameBoard {
  public signals = {
    onEventProcessed: new Signal<IEvent>(),
  };

  private eventQueue: IEvent[] = [];

  private readonly rnd: RandomDataGenerator;
  private data: IGameData;

  private readonly factory: BubbleFactory;

  constructor(seed?: string | any[]) {
    this.rnd = new RandomDataGenerator(seed || [Math.random()]);
    this.factory = new BubbleFactory(this.rnd);
  }

  public resize(levelData: ILevelData): void {
    this.data = parseLevel(this.data, this.rnd, this.factory, levelData);
  }

  public getData(): IGameData {
    return this.data;
  }

  public getEventsQueue(): IEvent[] {
    return this.eventQueue;
  }

  public getFactory(): BubbleFactory {
    return this.factory;
  }

  public getRDG(): RandomDataGenerator {
    return this.rnd;
  }

  public update(elapsedMs: number): void {
    if (this.data.state.firstShoot && !this.data.state.gameOver) {
      this.data.state.time += elapsedMs/1000;
      scrollGrid(this.data, this.factory, this.eventQueue);
    }

    handleBubblesInShootingSpace(this);
    // updateBubblesInSpace(this.data.shootingSpace, this.eventQueue);
    // checkShootedGridCollisions(this.data.shootingSpace, this.data.grid, this.eventQueue);
    resolveShootedBubbleGridCollisions(this);

    updateBubblesInSpace(this.data.popOutSpace, this.eventQueue);
    checkPopOutCollisions(this.data.popOutSpace, this.eventQueue);
    resolveBubbleBouncerCollisions(this);
    resolveBubblePointZoneCollisions(this);

    if (this.isPopOutCheckNeeded()) popOutUnhookedBubbles(this);

    checkDeathLineCrossing(this.data, this.eventQueue);
    handleChain(this);

    this.dispatchEvents();
  }

  private dispatchEvents(): void {
    if (this.eventQueue.length === 0) return;
    for (let i = 0; i < this.eventQueue.length; i++) {
      this.signals.onEventProcessed.dispatch(this.eventQueue[i]);
    }
    this.eventQueue.length = 0;
  }

  private isPopOutCheckNeeded(): boolean {
    return this.getEventsQueue().some((event) => {
      if (event.type === EventType.BubbleTransfered) {
        return (event as IEvent<EventType.BubbleTransfered>).data.fromPlace === Place.Grid;
      } else if (event.type === EventType.BubbleDestroyed) {
        return (event as IEvent<EventType.BubbleDestroyed>).data.bubble.place === Place.Grid;
      }
      return false;
    })
  }

  public shoot(hexSpaceTarget: IPoint): void {
    if (this.data.state.gameOver) return;
    shoot(this.data, this.factory, hexSpaceTarget, this.eventQueue);
  }

  public testShoot(hexSpaceTarget: IPoint): ITestShootResult {
    return testShoot(this.factory, this.data, hexSpaceTarget);
  }

  // DEBUG
  public fillRow(row: number): void {
    fillRow(this.data, this.factory, row, this.eventQueue);
  }
}